import { API_ROUTES } from '@/constants/apiRoutes';
import {
  BankAccountDetails,
  BankAccountEditFormValues,
  BankAccountsListResponse,
  LockReasonsFormValues,
  StatementsResponse,
  TaxDocumentsResponse,
} from '@/types/bankAccounts';
import { api, formatCurrencyForPost } from '@/utils/api';

export const getBankAccountsList = async (queryParams: string) => {
  const { data } = await api<BankAccountsListResponse>({
    url: `${API_ROUTES.BANK_ACCOUNTS}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const getBankAccountDetailsService = async (uuid: string) => {
  const { data } = await api<BankAccountDetails>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const updateBankAccountService = async (
  payload: BankAccountEditFormValues,
) => {
  const {
    isStaff,
    uuid,
    advisor,
    incomingDomesticFee,
    outgoingDomesticFee,
    savingsAccountFee,
    checkIssuingControls,
    wireControls,
    mobileCheckDepositControls,
    achCreditControls,
    achDebitControls,
  } = payload;

  if (isStaff) {
    await api<BankAccountDetails>({
      url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/staff-update`,
      method: 'put',
      version: 2,
      data: {
        advisor_id: advisor,
        wire_out_override_limit: wireControls.setLimit
          ? formatCurrencyForPost(wireControls.limit)
          : null,
        wire_out_override_limit_expires_at: wireControls.setLimit
          ? wireControls.expiration
          : null,
        check_deposit_override_limit: mobileCheckDepositControls.setLimit
          ? formatCurrencyForPost(mobileCheckDepositControls.limit)
          : null,
        check_deposit_override_limit_expires_at:
          mobileCheckDepositControls.setLimit
            ? mobileCheckDepositControls.expiration
            : null,
        check_issuance_override_limit: checkIssuingControls.setLimit
          ? formatCurrencyForPost(checkIssuingControls.limit)
          : null,
        check_issuance_override_limit_expires_at: checkIssuingControls.setLimit
          ? checkIssuingControls.expiration
          : null,
        ach_credit_override_limit: achCreditControls.setLimit
          ? formatCurrencyForPost(achCreditControls.limit)
          : null,
        ach_credit_override_limit_expires_at: achCreditControls.setLimit
          ? achCreditControls.expiration
          : null,
        ach_debit_override_limit: achDebitControls.setLimit
          ? formatCurrencyForPost(achDebitControls.limit)
          : null,
        ach_debit_override_limit_expires_at: achDebitControls.setLimit
          ? achDebitControls.expiration
          : null,
        fees: {
          incoming_domestic: incomingDomesticFee.useCompanySetting
            ? null
            : formatCurrencyForPost(incomingDomesticFee.fee),
          outgoing_domestic: outgoingDomesticFee.useCompanySetting
            ? null
            : formatCurrencyForPost(outgoingDomesticFee.fee),
          monthly_saving_account: savingsAccountFee.useCompanySetting
            ? null
            : formatCurrencyForPost(savingsAccountFee.fee),
        },
      },
    });
    return;
  }

  await api<BankAccountDetails>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/teammate-update`,
    method: 'put',
    version: 2,
    data: {
      advisor_id: advisor,
    },
  });
};

export const revealBankAccountNumberService = async (uuid: string) => {
  const { data } = await api<string>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/${API_ROUTES.REVEAL_ACCOUNT_NUMBER}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const lockBankAccountService = async (data: LockReasonsFormValues) => {
  const { status } = await api<string>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${data.uuid}/lock`,
    method: 'post',
    version: 2,
    data: {
      lock_reason: data.lockReason,
    },
  });

  return status;
};

export const unlockBankAccountService = async (uuid: string) => {
  const { status } = await api<string>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/unlock`,
    method: 'post',
    version: 2,
  });

  return status;
};

export const closeBankAccountService = async (
  uuid: string,
  preflight: boolean,
) => {
  const { data } = await api<{ canClose: boolean }>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/close`,
    method: 'post',
    version: 2,
    data: {
      preflight,
    },
  });

  return data;
};

export const getStatementService = async (uuid: string) => {
  const { data } = await api<StatementsResponse>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/statements`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const getTaxDocumentsService = async (uuid: string, isStaff = false) => {
  const { data } = await api<TaxDocumentsResponse>({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/${isStaff ? 'tax-documents' : 'filed-tax-documents'}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const fileTaxDocumentService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.BANK_ACCOUNTS}${uuid}/tax-documents`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const depositSweepEnrollService = async (
  uuid: string,
  idempotencyKey: string,
) => {
  const { data } = await api<BankAccountDetails>({
    url: `${API_ROUTES.DEPOSIT_SWEEP}enroll/`,
    method: 'post',
    version: 2,
    data: {
      bank_account_id: uuid,
    },
    idempotencyKey,
  });

  return data;
};

export const depositSweepUnenrollService = async (
  uuid: string,
  idempotencyKey: string,
) => {
  const { data } = await api<BankAccountDetails>({
    url: `${API_ROUTES.DEPOSIT_SWEEP}unenroll/`,
    method: 'post',
    version: 2,
    data: {
      bank_account_id: uuid,
    },
    idempotencyKey,
  });

  return data;
};
